import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { observer, inject } from "mobx-react";
import SearchList from "./SearchList";
import { filterFamilyLog } from "../utils/searchProducts";

const SEARCH_DEBOUNCE_MS = 350;
const MAX_SUGGESTIONS = 20;

const Search = ({ productsStore, isRtl }) => {
  // The search-input value lives in the store so external navigation
  // actions (logo click, breadcrumbs, nav links, etc.) can reset it via
  // productsStore.clearSearch(). Local component state is reserved for UI
  // concerns that don't need to be shared.
  const searchField = productsStore.searchInputValue ?? "";
  const setSearchField = (v) => productsStore.setSearchInputValue(v);
  const [showSearchList, setShowSearchList] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!!productsStore.selectedProduct) {
      productsStore.setSearchedTerm("");
      setShowSearchList(false);
    }
  }, [productsStore, productsStore.selectedProduct]);

  // Safety-net: when the URL moves outside of /catalog entirely (e.g. /cart,
  // /my-orders), wipe the search state. Any navigation that happens inside
  // /catalog (including /catalog/:productId ↔ /catalog via breadcrumbs) is
  // handled by the explicit productsStore.clearSearch() calls in the
  // navigation handlers — we intentionally *don't* clear in that case here
  // because we can't tell a breadcrumb click from a typing-driven route
  // change just from the pathname.
  useEffect(() => {
    if (!location.pathname.startsWith("/catalog")) {
      productsStore.clearSearch();
      setShowSearchList(false);
    }
  }, [location.pathname, productsStore]);

  // Cancel any pending debounced search when the component unmounts.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // NOTE: do not run the search from a useEffect on `searchField`.
  // The Search component lives in the Header and mounts on every page, so a
  // mount-time effect would call navigate("/catalog") and bounce the user
  // off /catalog/:productId on refresh (React.StrictMode in dev also
  // double-invokes mount effects, which defeats any "first-run" guard).
  // Trigger the search only from real user events instead.
  const runSearch = (value) => {
    productsStore.setSearchedTerm(value);
    navigate("/catalog");
  };

  const scheduleSearch = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      runSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const cancelPendingSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  // On a product page (/catalog/:productId) we don't want to start
  // searching while the user is still typing — that would yank them off
  // the product they're reading after every keystroke. Instead we show a
  // typeahead dropdown with matches and wait for an explicit submit
  // (Enter key, the search button, or clicking a suggestion).
  const isOnProductPage = /^\/catalog\/[^/]+/.test(location.pathname);

  // Local typeahead suggestions for the product page. Computed from the
  // family log that's already been fetched (ProductPage loads it on mount),
  // so we don't need Catalog to be mounted to power the dropdown.
  const localSuggestions = useMemo(() => {
    if (!isOnProductPage || !searchField) return [];
    const source =
      (productsStore.familyLog && productsStore.familyLog.length > 0
        ? productsStore.familyLog
        : productsStore.familyLogList) || [];
    return filterFamilyLog(
      source,
      searchField,
      productsStore.isSearchByPart,
      i18n.language
    ).slice(0, MAX_SUGGESTIONS);
  }, [
    isOnProductPage,
    searchField,
    productsStore.familyLog,
    productsStore.familyLogList,
    productsStore.isSearchByPart,
    i18n.language,
  ]);

  const handleChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchField(value);

    if (isOnProductPage) {
      // Show the typeahead dropdown with matches, but don't navigate —
      // the user stays on the product page until they explicitly submit.
      setShowSearchList(!!value);
      return;
    }

    setShowSearchList(true);
    // Debounce the actual search so the catalog doesn't re-render on every
    // keystroke while the user is still typing / deleting characters.
    scheduleSearch(value);
  };

  const handleSearch = () => {
    // Explicit action (Enter / button click) — run immediately and cancel
    // any pending debounced call.
    cancelPendingSearch();
    if (!searchField) return;
    setShowSearchList(true);
    runSearch(searchField);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const onPick = (item, displayLabel) => {
    // Reflect the picked item inside the input so the user can see what
    // they selected (instead of their half-typed query or an empty field).
    cancelPendingSearch();
    setSearchField(displayLabel ?? item.ZACP_MPARTNAME ?? "");
    productsStore.setSearchedTerm("");
    setShowSearchList(false);
    navigate(`/catalog/${item.ZACP_MPARTNAME}`);
  };

  const dropdownList = isOnProductPage
    ? localSuggestions
    : !!productsStore.searchedTerm
      ? productsStore.filteredProducts
      : [];

  return (
    <SearchList
      showSearchList={showSearchList}
      setShowSearchList={setShowSearchList}
      list={dropdownList}
      value={searchField}
      searchTerm={searchField}
      isRtl={isRtl}
      handleChange={handleChange}
      handleKeyDown={handleKeyDown}
      handleSearch={handleSearch}
      onPick={onPick}
    />
  );
};
export default inject("productsStore")(observer(Search));
